<template>
    <div class="message-editor">
        <div class="palette">
            <h4>{{ $t("wp.messageTypes") }}</h4>
            <div class="items">
                <div class="item" @click="addResponseItem(Payloads.BOT_RESPONSE.TEXT)">
                    <span class="icon material-icons" style="font-size: 30px">text_fields</span>
                    <span class="title">{{ $t(`chatbotv2.${Payloads.BOT_RESPONSE.TEXT.type}`) }}</span>
                </div>
                <div class="item" @click="addResponseItem(Payloads.BOT_RESPONSE.RANDOM_TEXT)">
                    <span class="icon material-icons" style="font-size: 30px">shuffle</span>
                    <span class="title">{{ $t(`chatbotv2.${Payloads.BOT_RESPONSE.RANDOM_TEXT.type}`) }}</span>
                </div>
                <div class="item" @click="addResponseItem(Payloads.BOT_RESPONSE.IMAGE)">
                    <span class="icon material-icons" style="font-size: 30px">image</span>
                    <span class="title">{{ $t(`chatbotv2.${Payloads.BOT_RESPONSE.IMAGE.type}`) }}</span>
                </div>
                <div class="item" @click="addResponseItem(Payloads.BOT_RESPONSE.GALLERY)">
                    <span class="icon material-icons" style="font-size: 30px">auto_awesome_motion</span>
                    <span class="title">{{ $t(`chatbotv2.${Payloads.BOT_RESPONSE.GALLERY.type}`) }}</span>
                </div>
                <div class="item" @click="addResponseItem(Payloads.BOT_RESPONSE.BUTTON)">
                    <span class="icon material-icons" style="font-size: 30px">view_carousel</span>
                    <span class="title"> {{ $t(`chatbotv2.${Payloads.BOT_RESPONSE.BUTTON.type}`) }}</span>
                </div>
                <div class="item" @click="addResponseItem(Payloads.BOT_RESPONSE.QUICK_REPLY)">
                    <span class="icon material-icons" style="font-size: 30px">smart_button</span>
                    <span class="title">{{ $t(`chatbotv2.${Payloads.BOT_RESPONSE.QUICK_REPLY.type}`) }}</span>
                </div>
            </div>
        </div>
        <div class="body">
            <div class="payload-wrapper">
                <div v-for="(p, i) in messages" class="payload" :key="'payload-' + i + renderCount">
                    <div class="toolbar">
                        <button class="toolbar-btn" @click="messages.splice(i, 1)">
                            <md-icon style="color: #06a9f3">delete</md-icon>
                        </button>
                        <button class="toolbar-btn">
                            <md-icon style="color: #06a9f3">swap_vert</md-icon>
                        </button>
                    </div>

                    <TextMessage
                        v-model="p.text"
                        v-if="p.type === Payloads.BOT_RESPONSE.TEXT.type"
                        :preview-mode="false"
                        :allowed-attributes="attributes"
                        :whatsapp-mode="true"
                    />
                    <RandomTextMessage
                        v-else-if="p.type === Payloads.BOT_RESPONSE.RANDOM_TEXT.type"
                        :show-attributes="true"
                        :texts="p.texts"
                        :on-updated="(data) => updateItem(p, data)"
                        :on-attribute-added="$_.noop"
                        :whatsapp-mode="true"
                        :allowed-attributes="attributes"
                    />
                    <GalleryMessageEditor
                        v-else-if="p.type === Payloads.BOT_RESPONSE.GALLERY.type"
                        :show-attributes="true"
                        :cards="p.cards"
                        :on-updated="(data) => updateItem(p, data)"
                        :on-attribute-added="$_.noop"
                        :flatted-diagram="{}"
                        :whatsapp-mode="true"
                        :allowed-attributes="attributes"
                    />
                    <ImageBotResponse
                        v-else-if="p.type === Payloads.BOT_RESPONSE.IMAGE.type"
                        :default-image-url="p.imageUrl"
                        :default-asset-id="p.assetId"
                        :on-updated="(data) => updateItem(p, data)"
                        :whatsapp-mode="true"
                        :allowed-attributes="attributes"
                    />
                    <FileBotResponse
                        v-else-if="p.type === Payloads.BOT_RESPONSE.FILE.type"
                        :default-file-url="p.fileUrl"
                        :default-asset-id="p.assetId"
                        :on-updated="(data) => updateItem(p, data)"
                        :whatsapp-mode="true"
                        :allowed-attributes="attributes"
                    />

                    <ButtonMessageEditor
                        v-else-if="p.type === Payloads.BOT_RESPONSE.BUTTON.type"
                        v-model="messages[i]"
                        :flattedDiagram="[]"
                        :whatsapp-mode="true"
                        :allowed-attributes="attributes"
                    />
                    <QuickReplyMessageEditor
                        :flattedDiagram="[]"
                        v-else-if="p.type === Payloads.BOT_RESPONSE.QUICK_REPLY.type"
                        v-model="messages[i]"
                        :whatsapp-mode="true"
                        :allowed-attributes="attributes"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TextMessage from "../../../../Chatbot/pages/dashboard/blocks/bot-response/TextMessage";
import RandomTextMessage from "../../../../Chatbot/pages/dashboard/blocks/bot-response/RandomTextMessage";
import GalleryMessageEditor from "../../../../Chatbot/pages/dashboard/blocks/bot-response/GalleryMessageEditor";
import ImageBotResponse from "../../../../Chatbot/pages/dashboard/blocks/bot-response/ImageBotResponse";
import ButtonMessageEditor from "../../../../Chatbot/pages/dashboard/blocks/bot-response/ButtonMessageEditor";
import QuickReplyMessageEditor from "../../../../Chatbot/pages/dashboard/blocks/bot-response/QuickReplyMessageEditor";
import { Payloads } from "../../../../Chatbot/chatbot";
import log2 from "../../../../../../utils/log2";
import { generateRandomId } from "../../../../../../utils/session";
import _ from "lodash";
import { mapState } from "vuex";
import FileBotResponse from "../../../../Chatbot/pages/dashboard/blocks/bot-response/FileBotResponse";

const log = log2("MessageEditor");
export default {
    name: "MessageEditor",
    components: {
        TextMessage,
        RandomTextMessage,
        GalleryMessageEditor,
        ImageBotResponse,
        FileBotResponse,
        ButtonMessageEditor,
        QuickReplyMessageEditor,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        attributes: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        ...mapState({
            whatsappPlusEnabled: (state) => _.get(state.setting.settings, "whatsapp.plus.enabled", false),
        }),
    },
    data() {
        return {
            renderCount: 0,
            messages: _.cloneDeep(this.value),
            Payloads,
        };
    },
    methods: {
        openNotificationWithIcon(featureType, type = "warning") {
            const messages = {
                interactionMessage: {
                    message: "Whatsapp Plus Plan Required.",
                    description:
                        "Interaction message types only available on Whatsapp Plus Plan . You need to upgrade your plan to use this feature. ",
                },
            };
            const message = messages[featureType];
            const key = `plan`;
            this.$notification[type]({
                message: message.message,
                description: message.description,
                placement: "topRight",
                key,
                btn: (h) => {
                    return h(
                        "a-button",
                        {
                            props: {
                                type: "primary",
                                size: "small",
                            },
                            on: {
                                click: () => {
                                    log("clicked", this.$notification);
                                    this.$router.push("/integrations/whatsapp/plans");
                                    this.$notification.close(key);
                                },
                            },
                        },
                        "Change Plan"
                    );
                },
            });
        },
        updateItem(item, payload) {
            log("updateItem", { item, payload });
            Object.keys(payload).forEach((key) => {
                item[key] = payload[key];
            });
        },
        addResponseItem(item) {
            if (
                !this.whatsappPlusEnabled &&
                [Payloads.BOT_RESPONSE.QUICK_REPLY, Payloads.BOT_RESPONSE.BUTTON, Payloads.BOT_RESPONSE.GALLERY]
                    .map((x) => x.type)
                    .includes(item.type)
            ) {
                this.openNotificationWithIcon("interactionMessage");
                return;
            }
            log("addResponseItem", item);
            const newItem = _.cloneDeep(item);
            newItem.id = generateRandomId();
            this.messages.push(newItem);
        },
    },
    watch: {
        messages: {
            handler() {
                this.$emit("input", this.messages);
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.message-editor {
    //border: 1px solid red;
    display: flex;
    background: #dfdfdf;

    .ProseMirror {
        height: 100% !important;
        width: 100% !important;
        border: 1px solid transparent !important;
    }

    .palette {
        background: #fff;
        //border-radius: 8px;
        //box-shadow: 0 4px 20px #00000040;
        min-width: 250px;
        padding: 1rem;

        .items {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            grid-gap: 1rem;
        }

        .item {
            text-align: center;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            &:hover {
                color: #006cff;

                .img,
                .icon {
                    border: 1px solid #006cff;
                }
            }

            .icon {
                width: 80px;
                height: 80px;
                border: 1px solid #dedede;
                border-radius: 4px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
            }

            .title {
                margin-top: 5px;
            }

            .img {
                border: 1px solid #dedede;
                border-radius: 4px;
                min-height: 60px;
                margin-bottom: 6px;
            }
        }
    }

    .payload-wrapper {
        width: 100%;
        background: #dfdfdf;
        padding: 1rem;

        .payload:hover .attr-btn {
            opacity: 1;
            transform: translateY(0);
        }

        .attr-btn {
            border: none;
            background: #fff;
            position: absolute;
            bottom: -6px;
            right: 70px;
            border-radius: 50%;
            box-shadow: 0 0 6px #76767621;
            width: 32px;
            height: 32px;
            opacity: 0;
            transform: translateY(100%);
            transition: all 0.3s ease-in-out;
        }

        .actions-wrapper {
            display: flex;
            gap: 10px;
        }
    }

    .icon-preview-block-modal {
        font-size: 19px;
        padding-top: 1px;
    }

    .header {
        padding: 1rem 1.5rem 1rem 2rem;
        margin-left: 2rem;
        background: #fff;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;

        .left {
            .type {
                color: #919ead;
                font-weight: 600;
                text-transform: uppercase;
            }

            .name {
                font-weight: bold;
                font-size: 1.5rem;
            }

            .custom-block-logo {
                width: 24px;
                height: 24px;
            }
        }

        .right {
            position: absolute;
            top: 0.5rem;
            right: 1rem;
            display: flex;
            gap: 0.5rem;

            .chatbot-btn {
                border: none;
                background: #dfdfdf;
                border-radius: 4px;
                padding: 4px 8px;
                margin: 0;
                min-height: 34px;
                min-width: 34px;
                color: #000;
                display: flex;
                justify-content: center;
                align-items: center;

                &.bordered {
                    border: 1px solid #000;
                }
            }
        }
    }

    .body {
        //padding-left: 2rem;
        //max-height: calc(100vh - 140px);
        //overflow-y: scroll;
        width: 100%;

        &::-webkit-scrollbar {
            display: none;
        }

        .payload-wrapper {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            background: #dfdfdf;
            padding: 1rem 0;
            min-height: 400px;
        }

        .payload {
            padding: 0 1.5rem;
            position: relative;
            border-bottom: 1px dashed #fff;
            margin: 10px 0;
            padding-bottom: 30px;

            input[type="checkbox"] {
                display: none;

                & + input {
                    display: none;
                }

                &:checked + input {
                    display: block;
                }
            }

            textarea {
                width: 100%;
                resize: none;
                height: 100px;
                border: 1px solid transparent;
                border-radius: 12px;
                padding: 6px 12px;

                &::-webkit-scrollbar {
                    display: none;
                }

                &:focus {
                    border-color: #06a9f3;
                }
            }

            label {
                display: flex;
                gap: 1rem;
                align-items: center;

                span {
                    background: #888888;
                    padding: 0.4rem 0.7rem;
                    border-radius: 1rem;
                    color: #ffffff;
                    user-select: none;
                    transition: all 0.3s ease-in-out;
                    cursor: pointer;
                    min-width: 100px;
                    text-align: center;

                    &:hover {
                        background: #a2a2a2;
                    }
                }

                .attr-val {
                    width: 100%;
                    resize: none;
                    border: 1px solid #a2a2a2;
                    border-radius: 2px;
                    padding: 0.3rem 0.9rem;

                    &:focus {
                        border: 1px solid #0651f3;
                    }
                }
            }

            .toolbar {
                opacity: 0;
                transition: all 0.3s ease-in-out;
                position: absolute;
                left: -17px;
                bottom: 0;
                top: 28px;
                display: flex;
                flex-direction: column;
                //justify-content: center;
                gap: 0.6rem;

                .toolbar-btn {
                    border: none;
                    background: #ffffff;
                    border-radius: 50%;
                    margin: 0;
                    height: 34px;
                    width: 34px;
                    color: #000;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    box-shadow: 0 4px 20px #00000040;

                    &.bordered {
                        border: 1px solid #000;
                    }
                }
            }

            &:hover .toolbar {
                opacity: 1;
            }

            .chatbot-btn {
                border: none;
                background: #bd5555;
                border-radius: 4px;
                padding: 4px 8px;
                margin: 0;
                min-height: 34px;
                min-width: 34px;
                color: #000;
                display: flex;
                justify-content: center;
                align-items: center;
                position: absolute;
                right: 10px;
                top: 60px;

                i {
                    color: #ffffff;
                }

                &.bordered {
                    border: 1px solid #000;
                }
            }

            .keywords {
                display: flex;
                flex-wrap: wrap;
                max-width: 430px;
                gap: 0.4rem;
                margin: 1rem 0;

                .keyword {
                    border: 1px dashed #aaa;
                    border-radius: 1.5rem;
                    padding: 0.5rem 1rem;
                    background: transparent;
                    text-align: center;
                    transition: all 0.3s ease-in-out;
                    font-weight: bold;
                    max-width: 80px;

                    &::placeholder {
                        color: #000;
                    }

                    &:focus {
                        border: 1px solid;
                    }

                    &.filled:not(:focus) {
                        border: 1px solid #0651f3;
                        background-color: #0651f3;
                        color: #ffffff;
                    }
                }
            }

            .says {
                display: flex;
                flex-direction: column;
                gap: 0.4rem;
                margin: 1rem 0;

                .say {
                    width: 100%;
                    resize: none;
                    border: 1px solid transparent;
                    border-radius: 12px;
                    padding: 14px;

                    &:focus {
                        border: 1px solid #0651f3;
                    }
                }
            }
        }

        .cbcard {
            background-color: #ffffff;
            border-radius: 4px;
            margin: 0 1.5rem;
            padding: 1rem;
        }

        .cbcard-btn {
            border: none;
            background: #fff;
            align-self: center;
            border-radius: 50%;
            height: 36px;
            transform: scale(0.7);

            &::before {
                content: "";
                width: 2px;
                height: 1.9rem;
                display: block;
                z-index: -3;
                position: absolute;
                bottom: 100%;
                left: 50%;
                transform: translateX(-50%);
                border: 1px dashed #888888;
            }
        }

        .condition-card {
            display: flex;
            margin-bottom: 1rem;
        }

        .cbcondition-btn {
            width: 100%;
            padding: 8px 15px;
            border-radius: 4px;
            border: 1px solid #dadada;
            transition: all 0.3s ease-in-out;
            background-color: #ffffff;

            &:hover {
                border: 1px solid #06a9f3;
                color: #06a9f3;
            }
        }
    }

    .ant-divider-horizontal.ant-divider-with-text-center::before,
    .ant-divider-horizontal.ant-divider-with-text-center::after {
        border-top: 1px solid #a8a8a8;
    }
}
</style>
